import { useEffect, useState } from 'react'
import { db } from './config'
import { client } from '../hooks/Client'
import describeQueryError from '../helpers/firestore/describeQueryError'
import notDeleted from '../helpers/notDeleted'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Shared cross-consumer subscription cache for queries with a single
// extra `.where(field, '==', value)` predicate on top of the standard
// `CompagnyID == client` filter.
//
// Mirrors `useSharedCollection` for the case where consumers all want the
// same filtered slice of a collection (e.g. every `Tasks/Type=addressVisit`
// caller across dashboard + live-report). Opens ONE onSnapshot per
// (collection × field × value × compagny) tuple; multicasts to all
// consumers; ref-counted teardown when the last consumer unmounts.
//
// Soft deletes (ADR-008) are filtered in the snapshot handler, for the same
// reason as in `useSharedCollection`: this hook bypasses the useFirestoreQuery
// engine, so nothing else strips deleted docs for its consumers.
// AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch

const cache = new Map()

const cacheKey = (collection, field, value) =>
    `${collection}|${field}|${String(value)}:${client}`

const ensureEntry = (collection, field, value) => {
    const key = cacheKey(collection, field, value)
    const existing = cache.get(key)
    if (existing) return existing

    const entry = {
        docs: undefined,
        listeners: new Set(),
        unsub: null,
    }
    entry.unsub = db
        .collection(collection)
        .where('CompagnyID', '==', client)
        .where(field, '==', value)
        .onSnapshot((snap) => {
            const next = []
            snap.forEach((d) => {
                const data = { ...d.data(), docid: d.id }
                if (notDeleted(data)) next.push(data)
            })
            entry.docs = next
            entry.listeners.forEach((cb) => cb(next))
        }, (error) => console.error(
            describeQueryError(collection, [['CompagnyID', '==', client], [field, '==', value]], error), error
        ))
    cache.set(key, entry)
    return entry
}

/**
 * @template T
 * @param {string} collection - Firestore collection name
 * @param {string} field - Field to filter on (equality)
 * @param {any} value - Value to match against `field`
 * @returns {Array<T> | undefined} - Cached docs (may be undefined on first paint)
 */
const useSharedCollectionWhere = (collection, field, value) => {
    const [docs, setDocs] = useState(() => {
        const entry = cache.get(cacheKey(collection, field, value))
        return entry?.docs
    })

    useEffect(() => {
        const entry = ensureEntry(collection, field, value)
        const cb = (next) => setDocs(next)
        entry.listeners.add(cb)
        if (entry.docs !== undefined) cb(entry.docs)

        return () => {
            entry.listeners.delete(cb)
            if (entry.listeners.size === 0) {
                entry.unsub?.()
                cache.delete(cacheKey(collection, field, value))
            }
        }
    }, [collection, field, value])

    return docs
}

export default useSharedCollectionWhere
